import {LogoutOutlined, ShoppingOutlined, UserOutlined} from '@ant-design/icons';
import {Avatar, Button, Spin} from 'antd';
import {jwtDecode} from 'jwt-decode';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {RQGetUserDetails} from '../../reactQuery/apis';
import {logoutStart} from '../../redux/authAction';
import {selectAuth} from '../../redux/selectors';
import LoginScreen from '../LoginScreen/LoginScreen';

import {OrderHistory} from './OrderHistory';
import {PersonalInformation} from './PersonalInformation';

const UserHistory = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [userId, setUserId] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const {loggedIn} = useSelector(state => state.auth);
  const token = auth?.tokenData;

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'orders') {
      setActiveTab('orders');
    } else {
      setActiveTab('profile');
    }
  }, [searchParams]);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded?.userId);
      } catch {
        console.error('Invalid token');
        setUserId(null);
      }
    } else {
      setUserId(null);
    }
  }, [token]);

  const {data: userData, isLoading} = RQGetUserDetails(userId, token, {
    enabled: loggedIn && !!userId,
  });

  const handleLogout = () => {
    dispatch(logoutStart());
    navigate('/');
  };

  if (!loggedIn) {
    return <LoginScreen onClose={() => setShowLoginModal(false)} />;
  }

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-screen'}>
        <Spin />
      </div>
    );
  }

  return (
    <div className={'relative min-h-screen bg-gray-50'}>
      {/* Background */}
      <div
        className={'absolute inset-0 bg-cover bg-center opacity-20'}
        // style={{backgroundImage: 'url("/userbg.jpg")'}}
      />

      {/* Main Wrapper */}
      <div className={'relative z-10 flex justify-center m-2 px-4 py-8 lg:py-16'}>
        <div className={'w-full max-w-5xl gap-6 flex flex-col lg:flex-row bg-white/80 backdrop-blur  overflow-hidden'}>
          
          {/* Sidebar */}
          <div className={'w-full h-96 lg:w-64 p-6  bg-primary-50 shadow-md rounded-md '}>
            <div className={'flex flex-col items-center gap-4'}>
              <Avatar size={80} className={'bg-blue-800 text-white text-2xl'}>
                {userData?.firstName?.charAt(0)}
                {userData?.lastName?.charAt(0)}
              </Avatar>

              <div className={'text-center'}>
                <h4 className={'text-xl font-semibold capitalize'}>
                  {userData?.firstName} {userData?.lastName}
                </h4>
                <p className={'text-gray-500 text-sm mt-1'}>{userData?.email}</p>
              </div>

              <div className={'flex flex-col gap-3 w-full mt-6'}>
                <Button
                  onClick={() => setActiveTab('profile')}
                  className={`flex items-center justify-center gap-2 ${
                    activeTab === 'profile' ? 'bg-blue-800 hover:!bg-primary-900 text-white' : 'border border-primary-800 text-primary-950'
                  }`}
                  block
                >
                  <UserOutlined /> My Profile
                </Button>

                <Button
                  onClick={() => setActiveTab('orders')}
                  className={`flex items-center justify-center gap-2 ${
                    activeTab === 'orders' ? 'bg-blue-800 text-white' : 'border border-primary-800 text-primary-950'
                  }`}
                  block
                >
                  <ShoppingOutlined /> My Orders
                </Button>
              </div>

              <Button
                onClick={handleLogout}
                className={'mt-6 w-full bg-red-600 hover:!bg-red-700 hover:!text-red-100 text-white flex items-center justify-center gap-2'}
              >
                <LogoutOutlined /> Log Out
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className={'flex-1 p-6 lg:p-8 rounded-md shadow-md bg-primary-50'}>
            {activeTab === 'profile' && <PersonalInformation />}
            {activeTab === 'orders' && <OrderHistory />}
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {showLoginModal && <LoginScreen onClose={() => setShowLoginModal(false)} />}
    </div>
  );
};

export default UserHistory;
